import { Input, SegmentedControl } from '@mantine/core';
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';

type SegmentedControlFieldOptionType = {
  value: string | boolean;
  label: string;
};

type SegmentedControlFieldPropsType<T extends FieldValues> = {
  fieldName: Path<T>;
  label: string;
  options: SegmentedControlFieldOptionType[];
};

// SegmentedControl only supports string values, so options are keyed by index
// to also allow boolean values. An unmatched value (e.g. null) is passed as ''
// so the control stays controlled and selects no segment.
export const SegmentedControlField = <T extends FieldValues>({
  fieldName,
  label,
  options,
}: SegmentedControlFieldPropsType<T>): React.ReactElement => {
  const { control } = useFormContext<T>();

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const selectedIndex = options.findIndex(
          (option) => option.value === value
        );

        return (
          <Input.Wrapper label={label} error={error?.message}>
            <div>
              <SegmentedControl
                value={selectedIndex === -1 ? '' : String(selectedIndex)}
                onChange={(index) => onChange(options[Number(index)].value)}
                data={options.map((option, index) => ({
                  value: String(index),
                  label: option.label,
                }))}
              />
            </div>
          </Input.Wrapper>
        );
      }}
    />
  );
};
